import { createReducer, on } from '@ngrx/store';
import { FoodGuideItem, FoodGuideAuditEntry } from '../model/food-guide.model';
import {
  ClearState,
  FetchFoodGuideListRequest,
  FetchFoodGuideListSuccess,
  FetchFoodGuideListFailure,
  FetchFoodGuideItemRequest,
  FetchFoodGuideItemSuccess,
  FetchFoodGuideItemFailure,
  CreateFoodGuideItemRequest,
  CreateFoodGuideItemSuccess,
  CreateFoodGuideItemFailure,
  UpdateFoodGuideItemRequest,
  UpdateFoodGuideItemSuccess,
  UpdateFoodGuideItemFailure,
  RetireFoodGuideItemRequest,
  RetireFoodGuideItemSuccess,
  RetireFoodGuideItemFailure,
  UnretireFoodGuideItemRequest,
  UnretireFoodGuideItemSuccess,
  UnretireFoodGuideItemFailure,
  FetchFoodGuideHistoryRequest,
  FetchFoodGuideHistorySuccess,
  FetchFoodGuideHistoryFailure,
  FetchFoodCategoriesRequest,
  FetchFoodCategoriesSuccess,
  FetchFoodCategoriesFailure,
} from './food-guide.actions';

export interface FoodGuideState {
  foodGuideList: {
    list: FoodGuideItem[];
    total: number;
    pageNumber: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    loading: boolean;
  };
  selectedItem: FoodGuideItem | null;
  selectedItemLoading: boolean;
  auditHistory: {
    list: FoodGuideAuditEntry[];
    total: number;
    pageNumber: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    loading: boolean;
  };
  foodCategories: { key: number, value: string }[];
  saving: boolean;
  retiring: boolean;
}

const initialState: FoodGuideState = {
  foodGuideList: {
    list: [],
    total: 0,
    pageNumber: 1,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    loading: false,
  },
  selectedItem: null,
  selectedItemLoading: false,
  auditHistory: {
    list: [],
    total: 0,
    pageNumber: 1,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    loading: false,
  },
  foodCategories: [],
  saving: false,
  retiring: false,
};

export const reducer = createReducer(
  initialState,

  on(ClearState, () => initialState),

  // FETCH LIST
  on(FetchFoodGuideListRequest, (state) => ({
    ...state,
    foodGuideList: {
      ...state.foodGuideList,
      loading: true,
    },
  })),
  on(FetchFoodGuideListSuccess, (state, props) => ({
    ...state,
    foodGuideList: {
      ...props.data,
      loading: false,
    },
  })),
  on(FetchFoodGuideListFailure, (state) => ({
    ...state,
    foodGuideList: {
      ...state.foodGuideList,
      loading: false,
    },
  })),

  // FETCH SINGLE ITEM
  on(FetchFoodGuideItemRequest, (state) => ({
    ...state,
    selectedItem: null,
    selectedItemLoading: true,
  })),
  on(FetchFoodGuideItemSuccess, (state, props) => ({
    ...state,
    selectedItem: props.data,
    selectedItemLoading: false,
  })),
  on(FetchFoodGuideItemFailure, (state) => ({
    ...state,
    selectedItemLoading: false,
  })),

  // CREATE
  on(CreateFoodGuideItemRequest, (state) => ({
    ...state,
    saving: true,
  })),
  on(CreateFoodGuideItemSuccess, (state) => ({
    ...state,
    saving: false,
  })),
  on(CreateFoodGuideItemFailure, (state) => ({
    ...state,
    saving: false,
  })),

  // UPDATE
  on(UpdateFoodGuideItemRequest, (state) => ({
    ...state,
    saving: true,
  })),
  on(UpdateFoodGuideItemSuccess, (state) => ({
    ...state,
    saving: false,
  })),
  on(UpdateFoodGuideItemFailure, (state) => ({
    ...state,
    saving: false,
  })),

  // RETIRE
  on(RetireFoodGuideItemRequest, (state) => ({
    ...state,
    retiring: true,
  })),
  on(RetireFoodGuideItemSuccess, (state) => ({
    ...state,
    retiring: false,
  })),
  on(RetireFoodGuideItemFailure, (state) => ({
    ...state,
    retiring: false,
  })),

  // UNRETIRE
  on(UnretireFoodGuideItemRequest, (state) => ({
    ...state,
    retiring: true,
  })),
  on(UnretireFoodGuideItemSuccess, (state) => ({
    ...state,
    retiring: false,
  })),
  on(UnretireFoodGuideItemFailure, (state) => ({
    ...state,
    retiring: false,
  })),

  // AUDIT HISTORY
  on(FetchFoodGuideHistoryRequest, (state, {payload}) => ({
    ...state,
    auditHistory: {
      ...(payload.pageNumber === 1 ? initialState.auditHistory : state.auditHistory),
      loading: true,
    },
  })),
  on(FetchFoodGuideHistorySuccess, (state, props) => ({
    ...state,
    auditHistory: {
      ...props.data,
      loading: false,
    },
  })),
  on(FetchFoodGuideHistoryFailure, (state) => ({
    ...state,
    auditHistory: {
      ...state.auditHistory,
      loading: false,
    },
  })),

  // FOOD CATEGORIES
  on(FetchFoodCategoriesSuccess, (state, props) => ({
    ...state,
    foodCategories: props.data?.list || [],
  })),
);
